import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { SocketContext } from '../../App';
import { useAuth } from '../../context/AuthContext';
import { backendUrl } from '../../config/api';
import mapImage from '../../images/map.jpg';
import positionMapCoords from '../Gameboard/positions';
import pawnImages from '../../constants/pawnImages';

axios.defaults.withCredentials = true;

const C = {
    bg:     '#060b1f',
    card:   'rgba(13,19,50,0.65)',
    border: 'rgba(255,255,255,0.08)',
    cyan:   '#00d4ff',
    orange: '#f97316',
    green:  '#00c853',
    red:    '#ff3b30',
    amber:  '#ffb300',
    purple: '#a855f7',
};
const COLOR_HEX = { red: '#ef4444', blue: '#3b82f6', green: '#22c55e', yellow: '#eab308' };
const MODE_LABEL = {
    classic: '🎯 Classic', popular: '🔥 Popular', quick: '⚡ Quick',
    reverse: '🔁 Reverse', token_1: '1️⃣ 1-Token', token_2: '2️⃣ 2-Token',
    token_3: '3️⃣ 3-Token', no_cut: '✋ No Cut', team_2v2: '🤝 2v2 Team',
};

// ── Live Ludo Board (canvas) ──────────────────────────────────────────────────
function LiveBoard({ pawns = [], rolledNumber, movingColor }) {
    const canvasRef = useRef(null);
    const bgRef     = useRef(null);
    const imgCache  = useRef({});
    const loadedRef = useRef({ bg: false, pawns: {} });
    const pawnsRef  = useRef(pawns);
    const rolledRef = useRef(rolledNumber);

    const paint = useCallback(() => {
        const canvas = canvasRef.current;
        const bg     = bgRef.current;
        if (!canvas || !bg || !loadedRef.current.bg) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(bg, 0, 0, canvas.width, canvas.height);

        // dim overlay when game over (winner present via movingColor===null sentinel)
        (pawnsRef.current || []).forEach(pawn => {
            const coords = positionMapCoords[pawn.position];
            if (!coords) return;
            const { x, y } = coords;
            const img = imgCache.current[pawn.color];
            if (img?.complete && img.naturalWidth) {
                // glow ring for moving color
                if (pawn.color === movingColor) {
                    ctx.save();
                    ctx.shadowColor = COLOR_HEX[pawn.color] || '#fff';
                    ctx.shadowBlur  = 14;
                    ctx.drawImage(img, x - 17, y - 15, 35, 30);
                    ctx.restore();
                } else {
                    ctx.drawImage(img, x - 17, y - 15, 35, 30);
                }
            }
        });

        // rolled number indicator in center
        if (rolledRef.current) {
            const cx = 230, cy = 230;
            ctx.save();
            ctx.fillStyle = 'rgba(0,0,0,0.55)';
            ctx.beginPath(); ctx.arc(cx, cy, 22, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.font = 'bold 22px system-ui';
            ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
            ctx.fillText(rolledRef.current, cx, cy);
            ctx.restore();
        }
    }, [movingColor]);

    useEffect(() => { pawnsRef.current = pawns; rolledRef.current = rolledNumber; paint(); }, [pawns, rolledNumber, paint]);

    useEffect(() => {
        const bg = new Image();
        bg.src = mapImage;
        bg.onload = () => { loadedRef.current.bg = true; paint(); };
        bgRef.current = bg;

        Object.entries(pawnImages).forEach(([color, src]) => {
            if (color === 'grey') return;
            const img = new Image();
            img.src = src;
            img.onload = () => { loadedRef.current.pawns[color] = true; paint(); };
            imgCache.current[color] = img;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <canvas
            ref={canvasRef}
            width={460} height={460}
            style={{ width: '100%', maxWidth: 420, height: 'auto', borderRadius: 16, border: `2px solid rgba(255,255,255,0.1)`, display: 'block', margin: '0 auto' }}
        />
    );
}

// ── Pawn Detail Panel ─────────────────────────────────────────────────────────
function PawnDetailPanel({ room }) {
    const pawns   = room?.pawns || [];
    const players = room?.players || [];

    const getPlayerName = color => players.find(p => p.color === color)?.name || color;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {['red','blue','green','yellow'].map(color => {
                const colorPawns = pawns.filter(p => p.color === color);
                if (!colorPawns.length) return null;
                const clr       = COLOR_HEX[color];
                const player    = players.find(p => p.color === color);
                const isMoving  = player?.nowMoving;
                const isWinner  = color === room.winner || player?.name === room.winner;
                const homeCount = colorPawns.filter(p => p.position >= 68).length;
                const onBoard   = colorPawns.filter(p => p.position < 68 && p.position !== p.basePos).length;
                const inBase    = colorPawns.filter(p => p.position === p.basePos).length;
                const lives     = player?.lives;

                return (
                    <div key={color} style={{
                        padding: '12px 14px', borderRadius: 12,
                        background: isMoving ? `${clr}18` : isWinner ? `${clr}12` : 'rgba(255,255,255,0.03)',
                        border: `1.5px solid ${isMoving ? clr : isWinner ? clr + '66' : clr + '33'}`,
                        boxShadow: isMoving ? `0 0 14px ${clr}33` : 'none',
                        transition: 'all 0.3s',
                    }}>
                        {/* row 1: name + badges */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{ width: 11, height: 11, borderRadius: '50%', background: clr, boxShadow: isMoving ? `0 0 8px ${clr}` : 'none', flexShrink: 0 }} />
                            <span style={{ fontWeight: 800, fontSize: 13, color: isMoving ? '#fff' : clr, flex: 1 }}>
                                {isWinner && '🏆 '}{getPlayerName(color)}
                            </span>
                            {room.vsBot && room.botColors?.includes(color) && (
                                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 8, background: 'rgba(255,107,0,0.2)', color: C.orange, fontWeight: 700 }}>BOT</span>
                            )}
                            {isMoving && (
                                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 8, background: `${clr}30`, color: clr, fontWeight: 700, animation: 'pulse 1s infinite' }}>▶ TURN</span>
                            )}
                            {isWinner && (
                                <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 8, background: 'rgba(255,179,0,0.2)', color: C.amber, fontWeight: 700 }}>WINNER</span>
                            )}
                        </div>

                        {/* row 2: stats */}
                        <div style={{ display: 'flex', gap: 10, fontSize: 11, marginBottom: 8, flexWrap: 'wrap' }}>
                            <span style={{ color: C.green,  fontWeight: 700 }}>🏠 Home: {homeCount}</span>
                            <span style={{ color: C.cyan,   fontWeight: 700 }}>🎯 Board: {onBoard}</span>
                            <span style={{ color: 'rgba(255,255,255,0.4)' }}>🔵 Base: {inBase}</span>
                            {lives != null && (
                                <span style={{ marginLeft: 'auto' }}>
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <span key={i} style={{ fontSize: 11, filter: i < lives ? 'none' : 'grayscale(1) opacity(0.3)' }}>
                                            {i < lives ? '❤️' : '🖤'}
                                        </span>
                                    ))}
                                </span>
                            )}
                        </div>

                        {/* row 3: individual pawn positions */}
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {colorPawns.map((p, i) => {
                                const isHome = p.position >= 68;
                                const isBase = p.position === p.basePos;
                                return (
                                    <span key={i} style={{
                                        fontSize: 10, padding: '3px 8px', borderRadius: 6, fontWeight: 600,
                                        background: isHome ? `${clr}35` : isBase ? 'rgba(255,255,255,0.07)' : `${clr}18`,
                                        color: isHome ? clr : isBase ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.75)',
                                        border: `1px solid ${isHome ? clr + '55' : isBase ? 'rgba(255,255,255,0.1)' : clr + '33'}`,
                                    }}>
                                        {isHome ? '🏠 Home' : isBase ? `Base` : `Sq ${p.position}`}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function GameWatchPage() {
    const { roomId } = useParams();
    const location = useLocation();
    const socket = useContext(SocketContext);
    const { user, isLoggedIn, isAdmin, isApiManager } = useAuth();

    const [status,   setStatus]   = useState('connecting');
    const [roomInfo, setRoomInfo] = useState(null);
    const [roomData, setRoomData] = useState(null);
    const [errMsg,   setErrMsg]   = useState('');

    const [showDetails,  setShowDetails]  = useState(true);
    const [showBoard,    setShowBoard]    = useState(true);

    const [chatAccess,    setChatAccess]    = useState(false);
    const [voiceAccess,   setVoiceAccess]   = useState(false);
    const [accessMsg,     setAccessMsg]     = useState('');
    const [accessLoading, setAccessLoading] = useState(null);

    const purchaseAccess = async type => {
        setAccessLoading(type); setAccessMsg('');
        try {
            const res = await axios.post(`${backendUrl}/api/rooms/${roomId}/purchase-access`, { type });
            if (type === 'chat')  setChatAccess(true);
            if (type === 'voice') setVoiceAccess(true);
            setAccessMsg(
                res.data.freeAccess
                    ? `${type === 'chat' ? '💬 Chat' : '🎙 Voice'} unlocked for free`
                    : `${type === 'chat' ? '💬 Chat' : '🎙 Voice'} unlocked! Balance: ${res.data.creditsBalance} cr`
            );
        } catch (e) {
            setAccessMsg(e.response?.data?.message || 'Purchase failed');
        } finally { setAccessLoading(null); }
    };

    const watchPassword = new URLSearchParams(location.search).get('password') || '';
    const watchLoginHref = `/auth/login?redirect=${encodeURIComponent(`/watch/${roomId}${location.search || ''}`)}`;
    const hasFreeSpectatorPremium = Boolean(
        user &&
        (isAdmin || (isApiManager && roomData?.creatorType === 'api' && roomData?.creatorUserId === user.id))
    );

    useEffect(() => {
        if (!socket || !roomId) return;
        const handleJoined   = info => { setRoomInfo(info); setStatus('joined'); };
        const handleRoomData = raw  => {
            try {
                const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
                setRoomData(data);
                if (status !== 'joined') setStatus('joined');
            } catch (_) {}
        };
        const handleError = err => { setErrMsg(err?.message || 'Could not join spectator view'); setStatus('error'); };

        socket.on('spectate:joined', handleJoined);
        socket.on('room:data',       handleRoomData);
        socket.on('error',           handleError);
        socket.emit('spectate:join', { roomId, password: watchPassword });
        return () => {
            socket.off('spectate:joined', handleJoined);
            socket.off('room:data',       handleRoomData);
            socket.off('error',           handleError);
        };
    }, [socket, roomId]); // eslint-disable-line react-hooks/exhaustive-deps

    const room = roomData || roomInfo;
    const movingColor = room?.players?.find(p => p.nowMoving)?.color || null;

    useEffect(() => {
        if (!hasFreeSpectatorPremium) return;
        setChatAccess(true);
        setVoiceAccess(true);
    }, [hasFreeSpectatorPremium]);

    return (
        <div style={{
            minHeight: '100vh',
            background: `${C.bg} radial-gradient(ellipse 100% 40% at 50% 0%, rgba(0,212,255,0.06) 0%, transparent 60%)`,
            color: '#fff', fontFamily: 'system-ui, sans-serif', display: 'flex', flexDirection: 'column',
        }}>
            {/* ── header ── */}
            <header style={{
                position: 'sticky', top: 0, zIndex: 30,
                background: 'rgba(6,11,31,0.94)', backdropFilter: 'blur(20px)',
                borderBottom: `1px solid ${C.border}`,
                padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 52, gap: 10,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>👁️</span>
                    <span style={{ fontWeight: 800, fontSize: 15, background: `linear-gradient(90deg,${C.cyan},#fff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        Watch Mode
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', background: 'rgba(0,212,255,0.08)', border: `1px solid rgba(0,212,255,0.2)`, padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                        SPECTATOR
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace', display: 'none' }}>{roomId}</span>
                    {true && (
                        <Link to={`/replay/${roomId}`} style={{
                            padding: '6px 14px', borderRadius: 9, border: `1px solid rgba(168,85,247,0.4)`,
                            background: 'rgba(168,85,247,0.1)', color: C.purple, fontSize: 12, fontWeight: 700, textDecoration: 'none',
                            display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
                        }}>
                            ▶ Replay
                        </Link>
                    )}
                    <Link to="/" style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecoration: 'none' }}>← Home</Link>
                </div>
            </header>

            <div style={{ flex: 1, padding: '20px 16px', maxWidth: 900, margin: '0 auto', width: '100%' }}>

                {/* connecting */}
                {status === 'connecting' && (
                    <div style={{ padding: '32px', borderRadius: 16, background: C.card, border: `1px solid ${C.border}`, textAlign: 'center', marginTop: 20 }}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
                        <div style={{ fontWeight: 700, fontSize: 16 }}>Connecting to game…</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>Room: {roomId}</div>
                    </div>
                )}

                {/* error */}
                {status === 'error' && (
                    <div style={{ padding: '28px', borderRadius: 16, background: 'rgba(255,59,48,0.07)', border: `1px solid rgba(255,59,48,0.3)`, textAlign: 'center', marginTop: 20 }}>
                        <div style={{ fontSize: 36, marginBottom: 12 }}>❌</div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: C.red }}>Could not join spectator view</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 8 }}>{errMsg}</div>
                    </div>
                )}

                {/* main content */}
                {status === 'joined' && room && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                        {/* ── room title row ── */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span style={{ fontSize: 22 }}>🎲</span>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 17 }}>{room.name || 'Game Room'}</div>
                                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginTop: 1 }}>
                                        {MODE_LABEL[room.gameMode] || room.gameMode} · {room.maxPlayers}P
                                        {room.roomCode && <span style={{ marginLeft: 8, fontFamily: 'monospace', color: C.amber }}>#{room.roomCode}</span>}
                                    </div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{
                                    padding: '5px 14px', borderRadius: 20, fontSize: 11, fontWeight: 800,
                                    background: room.winner ? 'rgba(255,179,0,0.15)' : room.started ? 'rgba(0,200,83,0.15)' : 'rgba(0,212,255,0.1)',
                                    color:      room.winner ? C.amber  : room.started ? C.green : C.cyan,
                                    border:     `1px solid ${room.winner ? 'rgba(255,179,0,0.3)' : room.started ? 'rgba(0,200,83,0.3)' : 'rgba(0,212,255,0.25)'}`,
                                }}>
                                    {room.winner ? '🏆 Game Over' : room.started ? '🟢 Live' : '⏳ Waiting'}
                                </span>
                                <span style={{
                                    padding: '5px 12px', borderRadius: 18, fontSize: 11, fontWeight: 800,
                                    background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.7)',
                                }}>
                                    👁 {room.players?.length || 0}/{room.maxPlayers || 0} players visible
                                </span>
                            </div>
                        </div>

                        {/* winner banner */}
                        {room.winner && (
                            <div style={{
                                padding: '16px 20px', borderRadius: 14, textAlign: 'center',
                                background: 'linear-gradient(135deg, rgba(255,179,0,0.18) 0%, rgba(255,107,0,0.1) 100%)',
                                border: '1.5px solid rgba(255,179,0,0.4)',
                                boxShadow: '0 0 32px rgba(255,179,0,0.2)',
                            }}>
                                <div style={{ fontSize: 32, marginBottom: 4 }}>🏆</div>
                                <div style={{ fontWeight: 900, fontSize: 22, color: C.amber, letterSpacing: 1 }}>
                                    {(room.winner?.toUpperCase?.() || room.winner)} wins!
                                </div>
                                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                                    {room.players?.find(p => p.color === room.winner || p.name === room.winner)?.name || ''}
                                </div>
                            </div>
                        )}

                        {/* ── 2-column layout: board left, details right ── */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,340px)', gap: 14, alignItems: 'start' }}>

                            {/* LEFT: live board */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {/* board toggle header */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '10px 16px', borderRadius: 12,
                                    background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}`,
                                }}>
                                    <div style={{ fontWeight: 800, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span>🗺️</span>
                                        <span>Live Board</span>
                                        {room.started && !room.winner && (
                                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, boxShadow: `0 0 8px ${C.green}`, display: 'inline-block', animation: 'pulse 1.5s infinite' }} />
                                        )}
                                    </div>
                                    <button onClick={() => setShowBoard(b => !b)} style={{
                                        padding: '4px 12px', borderRadius: 8, border: `1px solid ${C.border}`,
                                        background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)',
                                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                    }}>
                                        {showBoard ? '▲ Hide' : '▼ Show'}
                                    </button>
                                </div>

                                {showBoard && (
                                    <div style={{ padding: '16px', borderRadius: 16, background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}` }}>
                                        <LiveBoard
                                            pawns={room.pawns || []}
                                            rolledNumber={room.rolledNumber}
                                            movingColor={movingColor}
                                        />
                                        {/* current turn indicator */}
                                        {room.started && !room.winner && movingColor && (
                                            <div style={{ marginTop: 10, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                                                <span style={{ width: 10, height: 10, borderRadius: '50%', background: COLOR_HEX[movingColor], boxShadow: `0 0 8px ${COLOR_HEX[movingColor]}` }} />
                                                <span style={{ fontSize: 12, fontWeight: 700, color: COLOR_HEX[movingColor] }}>
                                                    {room.players?.find(p => p.color === movingColor)?.name || movingColor}'s turn
                                                </span>
                                                {room.rolledNumber && (
                                                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, background: 'rgba(255,255,255,0.1)', color: '#fff', fontWeight: 700 }}>
                                                        🎲 {room.rolledNumber}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {room.winner && (
                                            <div style={{ marginTop: 10, textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                                                Final board state
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* RIGHT: player + pawn details */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {/* details toggle header */}
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '10px 16px', borderRadius: 12,
                                    background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}`,
                                }}>
                                    <div style={{ fontWeight: 800, fontSize: 13 }}>♟️ Player Details</div>
                                    <button onClick={() => setShowDetails(b => !b)} style={{
                                        padding: '4px 12px', borderRadius: 8, border: `1px solid ${C.border}`,
                                        background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.6)',
                                        fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                    }}>
                                        {showDetails ? '▲ Hide' : '▼ Show'}
                                    </button>
                                </div>

                                {showDetails && (
                                    <div style={{ padding: '14px', borderRadius: 16, background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}` }}>
                                        <PawnDetailPanel room={room} />
                                    </div>
                                )}

                                {/* replay CTA */}
                                <Link to={`/replay/${roomId}`} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                                    padding: '14px 20px', borderRadius: 14, textDecoration: 'none',
                                    background: 'linear-gradient(135deg, rgba(168,85,247,0.25) 0%, rgba(99,102,241,0.18) 100%)',
                                    border: '1.5px solid rgba(168,85,247,0.4)',
                                    color: '#c4b5fd', fontWeight: 800, fontSize: 14,
                                    boxShadow: '0 0 20px rgba(168,85,247,0.15)',
                                    transition: 'all 0.2s',
                                }}>
                                    <span style={{ fontSize: 20 }}>▶</span>
                                    <div>
                                        <div>Watch Replay</div>
                                        <div style={{ fontSize: 10, fontWeight: 400, opacity: 0.7 }}>Step-by-step move history</div>
                                    </div>
                                </Link>

                                {/* spectator premium access */}
                                <div style={{ padding: '16px', borderRadius: 14, background: 'rgba(13,19,50,0.7)', backdropFilter: 'blur(14px)', border: `1px solid rgba(255,179,0,0.18)` }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 12 }}>💎 Spectator Features</div>
                                    {accessMsg && (
                                        <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12,
                                            background: accessMsg.includes('failed') || accessMsg.includes('Insufficient') ? 'rgba(255,59,48,0.1)' : 'rgba(0,200,83,0.1)',
                                            border:     `1px solid ${accessMsg.includes('failed') || accessMsg.includes('Insufficient') ? 'rgba(255,59,48,0.3)' : 'rgba(0,200,83,0.3)'}`,
                                            color:      accessMsg.includes('failed') || accessMsg.includes('Insufficient') ? C.red : C.green,
                                        }}>{accessMsg}</div>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                        {[
                                            { type: 'chat',  icon: '💬', label: 'Live Chat',  cost: hasFreeSpectatorPremium ? 0 : 5,  color: C.cyan,   hasAccess: chatAccess },
                                            { type: 'voice', icon: '🎙', label: 'Voice Chat', cost: hasFreeSpectatorPremium ? 0 : 10, color: C.orange, hasAccess: voiceAccess },
                                        ].map(({ type, icon, label, cost, color, hasAccess }) => (
                                            <div key={type} style={{ padding: '12px 14px', borderRadius: 11, background: hasAccess ? `${color}10` : 'rgba(255,255,255,0.03)', border: `1px solid ${hasAccess ? color + '44' : C.border}`, textAlign: 'center' }}>
                                                <div style={{ fontSize: 24 }}>{icon}</div>
                                                <div style={{ fontWeight: 700, fontSize: 12, marginTop: 5, color: hasAccess ? color : '#fff' }}>{label}</div>
                                                {hasAccess ? (
                                                    <div style={{ marginTop: 5, fontSize: 11, color, fontWeight: 700 }}>✓ Unlocked</div>
                                                ) : isLoggedIn ? (
                                                    <button onClick={() => purchaseAccess(type)} disabled={!!accessLoading}
                                                        style={{ marginTop: 7, padding: '6px 14px', borderRadius: 8, border: 'none', cursor: accessLoading ? 'wait' : 'pointer', background: `linear-gradient(135deg,${color},${color}aa)`, color: '#fff', fontWeight: 700, fontSize: 11, opacity: accessLoading ? 0.7 : 1 }}>
                                                        {accessLoading === type ? '⏳' : `${cost} cr`}
                                                    </button>
                                                ) : (
                                                    <Link to={watchLoginHref} style={{ display: 'block', marginTop: 7, fontSize: 11, color: C.amber, textDecoration: 'none', fontWeight: 700 }}>🔒 Login to unlock</Link>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {isLoggedIn && user && (
                                        <div style={{ marginTop: 10, textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
                                            Balance: <strong style={{ color: C.amber }}>{user.credits ?? 0} cr</strong>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* live pulse footer */}
                        {room.started && !room.winner && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, color: 'rgba(255,255,255,0.4)', padding: 8 }}>
                                <span style={{ width: 7, height: 7, borderRadius: '50%', background: C.green, boxShadow: `0 0 8px ${C.green}`, animation: 'pulse 1.5s infinite' }} />
                                Live — board updates automatically
                            </div>
                        )}
                    </div>
                )}
            </div>
            <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
        </div>
    );
}
